import React from 'react';
import { motion } from 'framer-motion';

/**
 * Reusable admin page banner component with consistent orange gradient design
 * @param {Object} props
 * @param {string} props.title - Main title text
 * @param {string} props.subtitle - Subtitle/description text
 * @param {React.Component} props.icon - Lucide icon component
 * @param {string} props.iconLabel - Label text next to icon (e.g., "Admin Panel")
 * @param {React.ReactNode} props.actions - Optional action buttons/elements to display on the right
 */
const AdminPageBanner = ({ title, subtitle, icon: Icon, iconLabel = 'Admin Panel', actions }) => {
  return (
    <div className="relative bg-gradient-to-r from-orange-500 via-orange-600 to-orange-700 text-white py-12 overflow-hidden">
      <div className="absolute inset-0 bg-black/10"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/20"></div>

      <div className="absolute top-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
      <div className="absolute bottom-10 right-10 w-40 h-40 bg-white/5 rounded-full blur-2xl"></div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full mb-4 border border-white/30">
            {Icon && <Icon className="w-5 h-5" />}
            <span className="text-sm font-semibold">{iconLabel}</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold mb-3">{title}</h1>
          <p className="text-lg text-orange-100 mb-6 max-w-2xl mx-auto">
            {subtitle}
          </p>

          {actions && (
            <div className="flex justify-center gap-4 mt-6">
              {actions}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminPageBanner;
